import type { FilePropertiesResponseDatagramBuf } from "~/classes/generated/response"

import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { DatagramBuf, DatagramErrorTypeBuf } from "~/classes/generated/datagram"
import { FilePropertiesRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf } from "~/utils/flatbuffers/entity"
import { parseFile } from "~/utils/flatbuffers/file"
import { addFileIdToBuf, parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/properties")

export function sendFilePropertiesRequests(requests: FilePropertiesRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)
  const propertiesRequests = []
  for (const request of requests) {
    const selection = addEntityPropertySelectionToBuf(fbb, request.selection)
    const pathSelection = addEntityPropertySelectionToBuf(fbb, request.pathSelection)
    const requestId = addRequestIdToBuf(fbb, request.requestId)
    const fileId = addFileIdToBuf(fbb, request.fileId)
    FilePropertiesRequestDatagramBuf.startFilePropertiesRequestDatagramBuf(fbb)
    FilePropertiesRequestDatagramBuf.addRequestId(fbb, requestId)
    FilePropertiesRequestDatagramBuf.addOffline(fbb, request.offline)
    FilePropertiesRequestDatagramBuf.addFileId(fbb, fileId)
    FilePropertiesRequestDatagramBuf.addSelection(fbb, selection)
    FilePropertiesRequestDatagramBuf.addPathSelection(fbb, pathSelection)
    const propertiesRequest = FilePropertiesRequestDatagramBuf.endFilePropertiesRequestDatagramBuf(fbb)

    RequestDatagramBuf.startRequestDatagramBuf(fbb)
    RequestDatagramBuf.addFilePropertiesRequest(fbb, propertiesRequest)
    propertiesRequests.push(RequestDatagramBuf.endRequestDatagramBuf(fbb))
  }

  const properties = DatagramBuf.createRequestVector(fbb, propertiesRequests)
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, properties)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleFilePropertiesResponseDatagramBuf(datagram: FilePropertiesResponseDatagramBuf) {
  onFilePropertiesResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    offline: datagram.offline(),
    fileId: parseFileId(datagram.fileId()!),
  })
}

export function onFilePropertiesResponse(response: FilePropertiesResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received file properties", clone(response))

  emitInterfaceEvent("filePropertiesResponse", response, response.requestId)

  if (response.error && response.error.type === DatagramErrorTypeBuf.FileNotFound && response.offline)
    console.debug("Offline file not found, waiting for server", response)
  else if (response.error)
    return onFilePropertiesErrored(response.error)
  if (!response.file)
    throw new Error("No file in properties response")

  const iFile = new InterfaceFile(response.file)
  emitEntityEvent("fileProperties", iFile.fileId, iFile.inner(), response.offline)

  const parentId = iFile.inferredParentId()
  if (!parentId)
    return
  emitEntityEvent("childProperties", parentId, iFile.inner(), response.offline)
}

function onFilePropertiesErrored(error: DatagramError) {
  // const { notifyDatagramError } = useAlerts()

  // TODO: only use notifications like this inside of callbacks
  // return notifyDatagramError(error, "File properties request was unsuccessful")
  console.error("File properties request failed", error)
}
